import React, { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Play, Gift, Sparkles } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useAds } from "../ads/AdsContext";
import { pushAd } from "../lib/ads";

/* Bloc AdSense réel — repli sur `fallback` (pub simulée) tant que non configuré/consenti. */
export const AdUnit = ({ slot, style, format, responsive = true, fallback = null }) => {
  const { client, ready } = useAds();
  const pushed = useRef(false);
  const active = ready && client && slot;

  useEffect(() => {
    if (active && !pushed.current) {
      pushed.current = true;
      pushAd();
    }
  }, [active]);

  if (!active) return fallback;

  return (
    <ins
      className="adsbygoogle"
      style={style}
      data-ad-client={client}
      data-ad-slot={slot}
      data-ad-format={format}
      data-full-width-responsive={responsive ? "true" : "false"}
      data-testid="adsense-unit"
    />
  );
};

const MockSide = () => {
  const { t } = useLang();
  return (
    <div className="flex h-[600px] w-[160px] flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-white/15 bg-white/[0.03] p-4 text-center">
      <Sparkles className="text-[#f093fb]" size={22} />
      <p className="font-display text-xs uppercase tracking-widest text-white/40">{t("adLabel")}</p>
      <p className="text-[11px] leading-relaxed text-white/30">{t("adSponsored")}</p>
      <div className="mt-2 h-px w-10 bg-white/10" />
      <p className="font-display text-lg font-bold text-white/20">160×600</p>
    </div>
  );
};

/* Bannière latérale (desktop uniquement) */
export const SideBanner = ({ side = "left" }) => {
  const { slots } = useAds();
  return (
    <div
      data-testid={`side-banner-${side}`}
      className="pointer-events-none hidden xl:flex fixed top-1/2 z-20 -translate-y-1/2 flex-col items-center"
      style={{ [side]: "3rem" }}
    >
      <AdUnit
        slot={slots.side}
        style={{ display: "inline-block", width: 160, height: 600 }}
        responsive={false}
        fallback={null}
      />
    </div>
  );
};

/* Bannière basse (mobile/tablette) */
export const BottomBanner = () => {
  const { t } = useLang();
  const { slots } = useAds();
  return (
    <div
      data-testid="bottom-banner"
      className="fixed inset-x-0 bottom-0 z-30 flex justify-center xl:hidden"
    >
      <div className="mx-auto w-full max-w-md px-2 pb-2">
        <AdUnit
          slot={slots.bottom}
          style={{ display: "block", minHeight: 60 }}
          format="horizontal"
          fallback={null}
        />
      </div>
    </div>
  );
};

/* Interstitiel plein écran — SIMULÉ (skip après compte à rebours) */
export const InterstitialAd = ({ open, onClose }) => {
  const { t } = useLang();
  const [count, setCount] = React.useState(5);

  useEffect(() => {
    if (!open) return;
    setCount(5);
    const id = setInterval(() => setCount((c) => (c <= 1 ? 0 : c - 1)), 1000);
    return () => clearInterval(id);
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-6"
          data-testid="interstitial-ad"
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            className="relative flex aspect-[4/5] w-full max-w-sm flex-col items-center justify-center gap-4 overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#26264a] to-[#141428] text-center"
          >
            <span className="absolute left-4 top-4 rounded-full bg-white/10 px-3 py-1 font-display text-[10px] uppercase tracking-widest text-white/50">
              {t("adLabel")}
            </span>
            <Sparkles className="animate-floaty text-[#f093fb]" size={48} />
            <p className="px-6 font-display text-xl font-bold text-white">Destin Express</p>
            <p className="px-8 text-sm text-white/50">{t("adSponsored")}</p>
            <button
              disabled={count > 0}
              onClick={onClose}
              data-testid="interstitial-skip"
              className="mt-4 rounded-full bg-white/10 px-6 py-2 text-sm font-semibold text-white transition-all disabled:opacity-40 enabled:hover:bg-white/20"
            >
              {count > 0 ? t("adSkipIn", { n: count }) : t("adSkip")}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* Vidéo récompensée — SIMULÉE (accorde un bonus) */
export const RewardedAd = ({ open, onClose, onReward }) => {
  const { t } = useLang();
  const [count, setCount] = React.useState(4);
  const [watching, setWatching] = React.useState(false);

  useEffect(() => {
    if (!open) {
      setWatching(false);
      setCount(4);
    }
  }, [open]);

  useEffect(() => {
    if (!watching) return;
    if (count === 0) {
      onReward?.();
      onClose?.();
      return;
    }
    const id = setTimeout(() => setCount((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [watching, count, onReward, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-6"
          data-testid="rewarded-ad"
        >
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            className="relative w-full max-w-sm rounded-3xl border border-white/10 bg-gradient-to-br from-[#26264a] to-[#141428] p-8 text-center"
          >
            {!watching && (
              <button
                onClick={onClose}
                data-testid="rewarded-close"
                className="absolute right-4 top-4 text-white/40 transition-colors hover:text-white"
              >
                <X size={20} />
              </button>
            )}
            <Gift className="mx-auto mb-4 animate-floaty text-[#38ef7d]" size={44} />
            <h3 className="font-display text-xl font-bold text-white">{t("rewardedTitle")}</h3>
            <p className="mt-3 text-sm text-white/60">{t("rewardedDesc")}</p>
            {watching ? (
              <div className="mt-6">
                <p className="font-display text-3xl font-black text-[#38ef7d]">{count}</p>
                <p className="mt-1 text-xs uppercase tracking-widest text-white/40">{t("adLabel")}</p>
              </div>
            ) : (
              <button
                onClick={() => {
                  setWatching(true);
                  setCount(4);
                }}
                data-testid="rewarded-watch"
                className="mt-6 flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#11998e] to-[#38ef7d] py-3 font-display font-bold text-white transition-transform active:scale-95"
              >
                <Play size={18} /> {t("rewardedGet")}
              </button>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
